import { Drawer } from "@mui/material";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { Suspense } from "react";
import { TenantFeatureFlagsProvider } from "routes/Staff/WasteFulfillment/ManifestReview/TenantFeatureFlagsProvider";

import { MarkManifestEpisodicButton } from "../EpisodicEvents/MarkManifestEpisodicButton";
import { LineItemDetailDialog } from "../ManifestLineItems/LineItemDetailDialog";
import { CompareSourcesPermalink } from "./CompareSourcesDialog";
import { LineItemsDataGrid } from "./LineItemsDataGrid";
import { ManifestDetailSection, ManifestDetailSectionSkeleton } from "./ManifestDetailSection";
import { ReviewPermalink } from "./ReviewPermalink";
import { useClearSectionParamOnClose } from "./useScrollToSectionParam";

interface ManifestDetailsProps {
  processedManifestId: string | undefined;
  open: boolean;
  onClose: () => void;
  /** Explicit tenant for cross-tenant (staff) callers off a tenant-scoped route. */
  tenantId?: string;
  /**
   * Fired once after the first successful discrepancy row mutation in the
   * drawer's or the `?review=discrepancies` dialog. Staff callers use it to
   * refetch queue data on close.
   */
  onDiscrepancyMutated?: () => void;
}

function MarkManifestEpisodicHeaderAction({
  processedManifestId,
  tenantId,
}: {
  processedManifestId: string | undefined;
  tenantId: string | undefined;
}) {
  if (!tenantId) {
    return <MarkManifestEpisodicButton processedManifestId={processedManifestId} />;
  }
  return (
    <TenantFeatureFlagsProvider tenantId={tenantId}>
      <MarkManifestEpisodicButton
        processedManifestId={processedManifestId}
        tenantIdOverride={tenantId}
      />
    </TenantFeatureFlagsProvider>
  );
}

export const ManifestDetails = ({
  processedManifestId,
  open,
  onClose,
  tenantId,
  onDiscrepancyMutated,
}: ManifestDetailsProps) => {
  useClearSectionParamOnClose(open);
  const {
    selectedId: wasteLineUnifiedId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("wasteLineUnifiedId");

  return (
    <>
      {/* URL-driven Compare Sources dialog (?compare=open|line:<id>|field:<name>)
          for the open manifest. Renders independently of the drawer. */}
      {/* URL-driven review dialogs (?review=document|discrepancies) for the open
          manifest: `document` opens the document-review dialog, `discrepancies`
          opens the discrepancies panel. The staff Manifest Review queue links here
          per row. Renders independently of the drawer; reads a disjoint param from
          CompareSourcesPermalink so the two never conflict. */}
      {processedManifestId && (
        <>
          <CompareSourcesPermalink
            processedManifestId={processedManifestId}
            canOpenReview
            tenantId={tenantId}
          />
          <ReviewPermalink
            processedManifestId={processedManifestId}
            onDiscrepancyMutated={onDiscrepancyMutated}
          />
        </>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "90%", sm: 720, md: 960, lg: 1200, xl: 1400 },
            boxSizing: "border-box",
          },
        }}
      >
        <Suspense fallback={<ManifestDetailSectionSkeleton />}>
          <ManifestDetailSection
            processedManifestId={processedManifestId}
            onClose={onClose}
            tenantId={tenantId}
            onDiscrepancyMutated={onDiscrepancyMutated}
            headerActions={
              <MarkManifestEpisodicHeaderAction
                processedManifestId={processedManifestId}
                tenantId={tenantId}
              />
            }
          >
            <LineItemsDataGrid
              processedManifestId={processedManifestId}
              onRowClick={pushIdToUrl}
              tenantId={tenantId}
            />
          </ManifestDetailSection>
        </Suspense>
      </Drawer>
      {/* Row click opens the line item as a modal (keyed per line so the form
          starts fresh each time); deep-linked via the wasteLineUnifiedId param. */}
      {wasteLineUnifiedId && (
        <Suspense fallback={null}>
          <LineItemDetailDialog
            key={wasteLineUnifiedId}
            processedManifestId={processedManifestId}
            lineItemId={wasteLineUnifiedId}
            onClose={popIdFromUrl}
            tenantId={tenantId}
          />
        </Suspense>
      )}
    </>
  );
};
